import {
  transformPoint,
  type Frame,
  type MeshBuilder,
  type VertexStyle,
} from '../mesh/meshBuilder';
import type { Vec3 } from '../../geo/types';
import type { LinearRGB } from '../../core/color';
import type { Chimney } from './types';
import type { Body } from './houseFacades';
import { boxSolid, orientedFace } from './solids';

/* Sources and counts: docs/plans/houses.md, "Chimneys". */
export const CHIMNEY = {
  /** A plain stack's width and depth. */
  stack: 0.6,
  /** Height of every stack's top over the ridge. */
  rise: 1.4,
  /** How far a stack's foot reaches under the roof at its lowest corner. */
  below: 0.05,
  wall: 0.12,
  recess: 0.45,
} as const;

export function chimneyMouths(
  frame: Frame,
  body: Body,
  chimneys: readonly Chimney[]
): readonly Vec3[] {
  return chimneys.map(({ at }) =>
    transformPoint([body.x0 + at * (body.depth - body.x0), body.ridge + CHIMNEY.rise, 0], frame)
  );
}

function openFlue(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  x: number,
  top: number
): void {
  const outer = CHIMNEY.stack / 2,
    inner = outer - CHIMNEY.wall;
  const corners = (half: number, y: number): Vec3[] => [
    [x - half, y, -half],
    [x + half, y, -half],
    [x + half, y, half],
    [x - half, y, half],
  ];
  const rim = corners(outer, top),
    lip = corners(inner, top),
    floor = corners(inner, top - CHIMNEY.recess);
  const soot = { ...style, color: style.color.map((v) => v * 0.12) as unknown as LinearRGB };
  for (let i = 0; i < 4; i++) {
    const next = (i + 1) % 4;
    orientedFace(builder, style, frame, [rim[i]!, rim[next]!, lip[next]!, lip[i]!], [0, 1, 0]);
    orientedFace(
      builder,
      soot,
      frame,
      [lip[i]!, lip[next]!, floor[next]!, floor[i]!],
      [x - (lip[i]![0] + lip[next]![0]) / 2, 0, -(lip[i]![2] + lip[next]![2]) / 2]
    );
  }
  orientedFace(builder, soot, frame, floor, [0, 1, 0]);
}

/**
 * Where a stack's foot must stand for its corners, `half` either side of the ridge, to sit inside the
 * roof: the steep slope falls `tan(pitch)` a metre across.
 */
function footOf(body: Body, half: number, pitch: number): number {
  return body.ridge - half * Math.tan(pitch) - CHIMNEY.below;
}

/** Each stack straddles the ridge `at` of the way from the body's front to its back. */
export function buildChimneys(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  body: Body,
  chimneys: readonly Chimney[],
  pitch: number
): void {
  const top = body.ridge + CHIMNEY.rise;
  for (const { at } of chimneys) {
    const x = body.x0 + at * (body.depth - body.x0);
    const half = CHIMNEY.stack / 2;
    boxSolid(
      builder,
      style,
      frame,
      [x - half, footOf(body, half, pitch), -half],
      [x + half, top, half],
      { top: false, bottom: false }
    );
    openFlue(builder, style, frame, x, top);
  }
}
